import React, { useMemo, useState, useCallback } from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Sector,
} from "recharts";
import { PIE_DEFAULT_COLORS } from "@/lib/chat/parsePieChartBlock";
import type { PieChartData } from "@/lib/chat/parsePieChartBlock";

interface PieChartCanvasProps {
  data: PieChartData;
  height?: number | "fill";
  mode?: "inline" | "expanded";
}

const ActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="hsl(var(--card))"
      strokeWidth={2}
      style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))", transition: "all 0.2s ease-out" }}
    />
  );
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const total = entry.payload._total as number;
  const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : "0";
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 shadow-sm">
      <p className="text-xs text-muted-foreground">
        <span
          className="mr-1.5 inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: entry.payload.fill }}
        />
        {entry.name}:{" "}
        <span className="font-medium text-foreground">
          {entry.value} ({pct}%)
        </span>
      </p>
    </div>
  );
};

const CustomLegend = ({ payload }: any) => {
  if (!payload?.length) return null;
  const rows: any[][] = [];
  for (let i = 0; i < payload.length; i += 2) {
    rows.push(payload.slice(i, i + 2));
  }
  return (
    <div className="flex flex-col items-center gap-1 pt-4">
      {rows.map((row, ri) => (
        <div key={ri} className="flex items-center justify-center gap-4">
          {row.map((entry: any, ci: number) => (
            <div key={ci} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[11px] text-muted-foreground">{entry.value}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const PieChartCanvas: React.FC<PieChartCanvasProps> = ({
  data,
  height = 300,
  mode = "inline",
}) => {
  const isFill = height === "fill";
  const containerHeight = isFill ? "100%" : height;
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const onMouseEnter = useCallback((_: any, index: number) => setActiveIndex(index), []);
  const onMouseLeave = useCallback(() => setActiveIndex(undefined), []);

  const chartData = useMemo(() => {
    const total = data.data.reduce((s, v) => s + v, 0);
    return data.labels.map((label, i) => ({
      name: label,
      value: data.data[i],
      fill: PIE_DEFAULT_COLORS[i % PIE_DEFAULT_COLORS.length],
      _total: total,
    }));
  }, [data]);

  return (
    <div
      className={`border-t border-border p-4 ${isFill ? "flex flex-col flex-1 min-h-0 h-full" : ""}`}
      style={{ backgroundColor: "hsl(var(--muted) / 0.2)" }}
    >
      <ResponsiveContainer
        width="100%"
        height={containerHeight}
        className={isFill ? "flex-1 min-h-0" : "py-4"}
      >
        <RechartsPieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={mode === "expanded" ? "85%" : "80%"}
            stroke="hsl(var(--card))"
            strokeWidth={2}
            animationDuration={700}
            label={false}
            labelLine={false}
            activeIndex={activeIndex}
            activeShape={ActiveShape}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={entry.fill}
                style={{ cursor: "pointer", transition: "opacity 0.2s ease-out" }}
                opacity={activeIndex !== undefined && activeIndex !== i ? 0.7 : 1}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};
